import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Trash, PaperPlaneTilt, Plus, X, CheckCircle, Clock, FilePlus, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api, formatApiErrorDetail, openPdf } from "../../lib/api";

const money = (v) => `${Number(v || 0).toFixed(2)} $`;

const blank = () => ({
  client_name: "", client_email: "", client_phone: "", client_address: "",
  items: [{ description: "", quantity: 1, unit_price: 0 }], tax_rate: 13, note: "",
});

export default function InvoicesPanel({ prefill, clearPrefill, onChanged }) {
  const [invoices, setInvoices] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/invoices");
      setInvoices(data);
    } catch { toast.error("Erreur de chargement"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (prefill) {
      setForm({
        client_name: prefill.client_name || "", client_email: prefill.client_email || "",
        client_phone: prefill.client_phone || "", client_address: "",
        items: prefill.items?.length ? prefill.items : [{ description: "", quantity: 1, unit_price: 0 }],
        tax_rate: prefill.tax_rate ?? 13, note: prefill.note || "", quote_id: prefill.quote_id,
      });
      setOpenForm(true);
      clearPrefill?.();
    }
  }, [prefill, clearPrefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateItem = (idx, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: 0 }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const subtotal = form.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const tax = subtotal * (Number(form.tax_rate) / 100);
  const total = subtotal + tax;

  const create = async () => {
    if (!form.client_name || !form.client_email) return toast.error("Nom et email requis");
    const items = form.items.filter((i) => i.description).map((i) => ({ description: i.description, quantity: Number(i.quantity) || 1, unit_price: Number(i.unit_price) || 0 }));
    if (!items.length) return toast.error("Ajoutez au moins une ligne");
    setSaving(true);
    try {
      await api.post("/admin/invoices", { ...form, items, tax_rate: Number(form.tax_rate) });
      setOpenForm(false);
      setForm(blank());
      await load();
      onChanged?.();
      toast.success("Facture créée");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const send = async (id) => {
    try {
      const { data } = await api.post(`/admin/invoices/${id}/send`);
      if (data.email?.status === "sent") toast.success("Facture envoyée par email");
      else if (data.email?.status === "skipped") toast.info("Email non configuré — facture prête");
      else toast.warning("Email: " + data.email?.status);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const togglePaid = async (inv) => {
    const status = inv.status === "paid" ? "unpaid" : "paid";
    await api.patch(`/admin/invoices/${inv.id}`, { status });
    await load();
    onChanged?.();
  };

  const remove = async (id) => {
    await api.delete(`/admin/invoices/${id}`);
    await load();
    onChanged?.();
    toast.success("Supprimé");
  };

  const pdf = async (id) => {
    try {
      await openPdf(`/admin/invoices/${id}/pdf`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur PDF");
    }
  };

  return (
    <div data-testid="invoices-panel">
      <div className="flex justify-end mb-4">
        <button onClick={() => { setForm(blank()); setOpenForm(true); }} className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-3 text-sm font-bold text-white hover:bg-forest-dark transition-colors" data-testid="new-invoice-btn">
          <FilePlus size={18} weight="bold" /> Nouvelle facture
        </button>
      </div>

      <div className="grid gap-3">
        {invoices.length === 0 && <div className="rounded-2xl border border-dashed border-forest/20 p-10 text-center text-forest/50">Aucune facture pour le moment.</div>}
        {invoices.map((inv, i) => (
          <motion.div
            key={inv.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white border border-forest/10 p-5"
            data-testid={`invoice-row-${i}`}
          >
            <div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs font-bold text-forest/50">{inv.number}</span>
                <span className="font-bold text-forest">{inv.client_name}</span>
                <Badge className={`border-0 ${inv.status === "paid" ? "bg-leaf/15 text-forest" : "bg-gold/15 text-gold-dark"}`}>
                  {inv.status === "paid" ? "Payée" : "Impayée"}
                </Badge>
              </div>
              <div className="mt-1 text-sm text-forest/60">{inv.client_email} · <span className="font-bold text-forest">{money(inv.total)}</span></div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => togglePaid(inv)} className="inline-flex items-center gap-1.5 rounded-full border border-forest/20 px-3 py-2 text-xs font-bold text-forest hover:bg-forest hover:text-white transition-colors" data-testid={`invoice-toggle-${i}`}>
                {inv.status === "paid" ? <Clock size={14} /> : <CheckCircle size={14} />}
                {inv.status === "paid" ? "Marquer impayée" : "Marquer payée"}
              </button>
              <button onClick={() => pdf(inv.id)} className="grid h-9 w-9 place-items-center rounded-full border border-forest/20 text-forest hover:bg-forest hover:text-white transition-colors" data-testid={`invoice-pdf-${i}`}>
                <FilePdf size={16} weight="fill" />
              </button>
              <button onClick={() => send(inv.id)} className="grid h-9 w-9 place-items-center rounded-full bg-gold text-white hover:bg-gold-dark transition-colors" data-testid={`invoice-send-${i}`}>
                <PaperPlaneTilt size={16} weight="fill" />
              </button>
              <button onClick={() => remove(inv.id)} className="grid h-9 w-9 place-items-center rounded-full border border-destructive/20 text-destructive hover:bg-destructive/10 transition-colors" data-testid={`invoice-delete-${i}`}>
                <Trash size={16} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-forest">Nouvelle facture</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Nom du client *</Label><Input value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className="rounded-lg h-10" data-testid="inv-name" /></div>
              <div className="space-y-2"><Label>Email *</Label><Input type="email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} className="rounded-lg h-10" data-testid="inv-email" /></div>
              <div className="space-y-2"><Label>Téléphone</Label><Input value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} className="rounded-lg h-10" data-testid="inv-phone" /></div>
              <div className="space-y-2"><Label>Adresse</Label><Input value={form.client_address} onChange={(e) => set("client_address", e.target.value)} className="rounded-lg h-10" data-testid="inv-address" /></div>
            </div>

            <div className="space-y-2">
              <Label>Lignes</Label>
              {form.items.map((it, idx) => (
                <div key={idx} className="flex gap-2 items-center" data-testid={`inv-item-${idx}`}>
                  <Input placeholder="Description" value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} className="rounded-lg h-10 flex-1" data-testid={`inv-desc-${idx}`} />
                  <Input type="number" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="rounded-lg h-10 w-16" data-testid={`inv-qty-${idx}`} />
                  <Input type="number" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} className="rounded-lg h-10 w-24" data-testid={`inv-price-${idx}`} />
                  <button onClick={() => removeItem(idx)} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-destructive hover:bg-destructive/10"><X size={16} /></button>
                </div>
              ))}
              <button onClick={addItem} className="inline-flex items-center gap-1.5 text-sm font-bold text-leaf hover:text-forest transition-colors" data-testid="inv-add-item"><Plus size={16} weight="bold" /> Ajouter une ligne</button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Taxes (%)</Label><Input type="number" value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} className="rounded-lg h-10" data-testid="inv-tax" /></div>
              <div className="rounded-xl bg-forest text-white p-4 text-right">
                <div className="text-xs text-white/60">Sous-total {money(subtotal)} · Taxes {money(tax)}</div>
                <div className="font-serif text-2xl">Total {money(total)}</div>
              </div>
            </div>

            <div className="space-y-2"><Label>Note</Label><Textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} className="rounded-xl" data-testid="inv-note" /></div>

            <button onClick={create} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-forest px-5 py-3.5 text-sm font-bold text-white hover:bg-forest-dark transition-colors disabled:opacity-60" data-testid="inv-create">
              {saving ? "Création..." : "Créer la facture"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
